import type * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import type * as chartJs from 'chart.js'

import * as color from '../../../../../color'
import * as utils from '../utils'

/**
 * Transform data from
 * {@link ents.core.display.Chart.Options<ents.core.display.chart.Type.BarChart>} to
 * {@link chartJs.ChartOptions<'bar'>}
 */
export function getOptions(
  {
    config,
    data,
  }: ents.core.display.chart.Chart<
    ents.core.display.chart.Type.Bar | ents.core.display.chart.Type.StackedBar
  >,
  {
    stacked = false,
  }: {
    readonly stacked?: boolean
  },
): chartJs.ChartOptions<'bar'> {
  const labels = Array.from(
    new Set(
      data?.values?.flatMap(
        value => value.points?.map(point => point.x ?? 0) ?? [],
      ) ?? [],
    ),
  )

  const groupNames = data?.values?.map((v, i) => v.name ?? `Group ${i}`) ?? []

  // Build matrix [xIndex][y-values by department]
  const valueMatrix: number[][] = labels.map(
    x =>
      data?.values?.map(({ points }) => points?.find(p => p.x === x)?.y ?? 0) ??
      [],
  )

  /**
   * Array of total positive stack values per category (x-axis label). Each
   * element represents the sum of all positive values from all datasets at the
   * same index.
   *
   * Example:
   *   If datasets = [
   *     { data: [10, -5, 15] },
   *     { data: [5, 10, -10] }
   *   ]
   *   Then positiveTotals = [
   *     10 + 5,     // = 15
   *     max(-5, 0) + 10, // = 10
   *     15 + max(-10, 0) // = 15
   *   ]
   *
   */
  const positiveTotals = stacked
    ? valueMatrix.map(row =>
        row.reduce((sum, val) => sum + Math.max(val, 0), 0),
      )
    : undefined

  /**
   * Array of total negative stack values per category (x-axis label). Each
   * element represents the sum of all negative values from all datasets at the
   * same index.
   *
   * Example:
   *   If datasets = [
   *     { data: [10, -5, 15] },
   *     { data: [5, 10, -10] }
   *   ]
   *   Then negativeTotals = [
   *     0,             // no negatives at index 0
   *     -5 + 0,        // = -5
   *     0 + -10        // = -10
   *   ]
   *
   */
  const negativeTotals = stacked
    ? valueMatrix.map(row =>
        row.reduce((sum, val) => sum + Math.min(val, 0), 0),
      )
    : undefined

  /** Extracts and flattens y-values from a nested data structure. */
  const yValues = valueMatrix.flat()

  /**
   * Calculated minimum value between 0 and all y-values or all
   *{@link negativeTotals}, in case that is type of stacked bar chart
   */
  const minValue =
    stacked && negativeTotals
      ? Math.min(...negativeTotals, 0)
      : Math.min(...yValues, 0)

  /**
   * Calculated maximum value between 0 and all y-values or all
   * {@link positiveTotals}, in case that is type of stacked bar chart
   */
  const maxValue =
    stacked && positiveTotals
      ? Math.max(...positiveTotals, 0)
      : Math.max(...yValues, 0)

  /**
   * Calculated magnitude to define a "clean" step size relative to the data
   * range.
   */
  const step = Math.pow(10, Math.floor(Math.log10(maxValue)))

  /**
   * Rounds the maximum value up to the next multiple of the step size.
   *
   * This ensures the Y-axis maximum ends at a "clean" value beyond the largest
   * data point, making charts easier to read with evenly spaced ticks.
   */
  const nextStepMax = Math.ceil(maxValue / step) * step

  let options: chartJs.ChartOptions<'bar'> = {
    ...utils.DEFAULT_OPTIONS,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: data?.values
          ? utils.getUniqueDatasetNames(data.values).size > 1
          : false,
      },
    },
    scales: {
      x: {
        stacked,
      },
      y: {
        beginAtZero: false,
        max: nextStepMax,
        min: minValue,
        stacked,
        ticks: {
          callback: function (value) {
            if (TypeChecker.isNumber(value) && value >= 1000000) {
              return value / 1000000 + 'M'
            }
            return value
          },
          stepSize: step,
        },
      },
    },
  }
  // In case of stacked bar chart with sorted values, we need to define custom
  // tooltip labels
  if (stacked) {
    options = {
      ...options,
      plugins: {
        ...options.plugins,
        tooltip: {
          ...options.plugins?.tooltip,
          callbacks: {
            label(context) {
              const xIndex = context.dataIndex
              const position = context.datasetIndex

              // Get values for this X label (i.e. row in matrix)
              const row = valueMatrix[xIndex] ?? []

              // Sort descending by value
              const sorted = row
                .map((value, i) => ({
                  name: groupNames[i],
                  value,
                }))
                .sort((a, b) => b.value - a.value)

              const entry = sorted[position]
              const name = entry?.name ?? 'N/A'
              const val = entry?.value ?? 0

              return `${name}: ${val.toLocaleString()}`
            },
          },
        },
      },
    }
  }
  /**
   * Will be equal to {@link config?.name} if available; otherwise, it defaults to
   * {@link data?.name}. If neither is defined, `undefined` is assigned.
   */
  const chartTitle = config?.name ?? data?.name
  if (chartTitle) {
    options = {
      ...options,
      plugins: {
        ...options.plugins,
        title: {
          align: 'center',
          color: color.BLACK,
          display: true,
          font: {
            weight: 'bold',
          },
          padding: {
            bottom: 20,
          },
          text: chartTitle,
        },
      },
    }
  }
  if (config) {
    const { beginAtZero, onItemClick, borderColor } = config
    if (data && onItemClick) {
      options = {
        ...options,
        onClick(_, elements) {
          const point = utils.getPointByIndex(data, elements, stacked)
          if (point) {
            onItemClick(point)
          }
        },
      }
    }
    if (borderColor) {
      options = {
        ...options,
        borderColor,
      }
    }
    if (beginAtZero) {
      options = {
        ...options,
        scales: {
          y: {
            ...options.scales?.y,
            beginAtZero,
          },
        },
      }
    }
  }
  return options
}
